import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import {
  FiFileText,
  FiBarChart2,
  FiTrendingUp,
  FiCalendar,
  FiActivity,
  FiMessageSquare,
  FiEye,
  FiLink,
  FiTrash2,
  FiStar
} from 'react-icons/fi';
import { supabase } from '../../supabase';
import CONFIG from '../../config';
import { createShareLink } from '../../lib/api/shareLinks';

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  CONFIG.API_BASE ||
  'http://localhost:4998';
const HISTORY_PAGE_SIZE = 20;

const HISTORY_TYPES = {
  screen: {
    label: 'Screen',
    icon: FiFileText,
    badgeClass: 'bg-amber-100 text-amber-700'
  },
  backtest: {
    label: 'Backtest',
    icon: FiBarChart2,
    badgeClass: 'bg-blue-100 text-blue-700'
  },
  optimizer: {
    label: 'Optimizer',
    icon: FiTrendingUp,
    badgeClass: 'bg-emerald-100 text-emerald-700'
  },
  seasonality: {
    label: 'Seasonality',
    icon: FiCalendar,
    badgeClass: 'bg-indigo-100 text-indigo-700'
  },
  chart: {
    label: 'Chart',
    icon: FiActivity,
    badgeClass: 'bg-rose-100 text-rose-700'
  },
  chat: {
    label: 'Chat Run',
    icon: FiMessageSquare,
    badgeClass: 'bg-cyan-100 text-cyan-700',
    timestampLabel: 'Last updated',
    deletePrompt: 'Delete this chat?'
  }
};

const SHARE_TYPES = {
  screen: 'screen_result',
  backtest: 'backtest',
  optimizer: 'backtest',
  seasonality: 'seasonality_result',
  chart: 'chart_result'
};

const DELETE_ENDPOINTS = {
  screen: (id) => `/api/screen-results/${id}`,
  backtest: (id) => `/api/backtests/${id}`,
  optimizer: (id) => `/api/backtests/${id}`,
  seasonality: (id) => `/api/seasonality-results/${id}`,
  chart: (id) => `/api/chart-results/${id}`,
  chat: (id) => `/api/chats/${id}`
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString();
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'N/A';
  return `${(num * 100).toFixed(2)}%`;
};

const formatNumber = (value, decimals = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'N/A';
  return num.toFixed(decimals);
};

const formatFocusLabel = (value) => {
  if (!value) return 'N/A';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatFormulaLabel = (formula) => {
  const trimmed = (formula || '').trim();
  if (!trimmed) return 'N/A';
  if (trimmed.length <= 36) return trimmed;
  return `${trimmed.slice(0, 33)}...`;
};

const formatChatPreview = (message, maxLength = 140) => {
  const cleaned = (message || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'No user messages yet.';
  if (cleaned.length <= maxLength) return cleaned;
  return `${cleaned.slice(0, maxLength - 3)}...`;
};

const HistorySection = ({ onChatDeleted, onChatSelected }) => {
  const navigate = useNavigate();
  const listRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState('');
  const [sharingId, setSharingId] = useState(null);
  const [shareFeedback, setShareFeedback] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [starringIds, setStarringIds] = useState({});
  const [starredOnly, setStarredOnly] = useState(false);
  const [editingTitleKey, setEditingTitleKey] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [savingTitleKeys, setSavingTitleKeys] = useState({});
  const [pages, setPages] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const titleCancelRef = useRef(false);

  const fetchHistoryPage = useCallback(async (cursor) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('No active session');
    const headers = { Authorization: `Bearer ${session.access_token}` };
    const params = new URLSearchParams();
    params.set('limit', String(HISTORY_PAGE_SIZE));
    if (starredOnly) {
      params.set('starred', '1');
    }
    if (cursor) {
      params.set('cursor', cursor);
    }
    const response = await fetch(`${API_BASE}/api/history?${params.toString()}`, { headers });
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload.message || 'Failed to load history');
    }
    return payload;
  }, [starredOnly]);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const payload = await fetchHistoryPage(null);
      setPages([{
        items: payload.items || [],
        nextCursor: payload.next_cursor || null
      }]);
      setPageIndex(0);
    } catch (err) {
      setError(err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [fetchHistoryPage]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const currentPage = pages[pageIndex] || { items: [], nextCursor: null };
  const pageItems = useMemo(() => currentPage.items || [], [currentPage.items]);
  const groupedItems = useMemo(() => {
    const groups = [];
    pageItems.forEach((item) => {
      const label = formatDate(item.created_at);
      const lastGroup = groups[groups.length - 1];
      if (!lastGroup || lastGroup.label !== label) {
        groups.push({ label, items: [item] });
      } else {
        lastGroup.items.push(item);
      }
    });
    return groups;
  }, [pageItems]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pageIndex]);

  const handleNextPage = useCallback(async () => {
    if (paging) return;
    const cached = pages[pageIndex + 1];
    if (cached) {
      setPageIndex((prev) => prev + 1);
      return;
    }
    if (!currentPage.nextCursor) return;
    try {
      setPaging(true);
      setError('');
      const payload = await fetchHistoryPage(currentPage.nextCursor);
      setPages((prev) => ([
        ...prev,
        {
          items: payload.items || [],
          nextCursor: payload.next_cursor || null
        }
      ]));
      setPageIndex((prev) => prev + 1);
    } catch (err) {
      setError(err.message || 'Failed to load history');
    } finally {
      setPaging(false);
    }
  }, [currentPage.nextCursor, fetchHistoryPage, pageIndex, pages, paging]);

  const handlePrevPage = useCallback(() => {
    setPageIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleChatSelect = useCallback((item) => {
    if (onChatSelected) {
      onChatSelected(item.id);
      return;
    }
    navigate('/dashboard', {
      state: {
        chatId: item.id
      }
    });
  }, [navigate, onChatSelected]);

  const handleView = (item) => {
    if (item.type === 'screen') {
      navigate(`/dashboard/strategies?screen_result=${item.id}`);
      return;
    }
    if (item.type === 'backtest' || item.type === 'optimizer') {
      navigate('/dashboard/strategies', {
        state: {
          loadBacktestId: item.id
        }
      });
      return;
    }
    if (item.type === 'seasonality') {
      navigate(`/dashboard/seasonality?result_id=${item.id}`);
      return;
    }
    if (item.type === 'chart') {
      navigate(`/dashboard/charts?result_id=${item.id}`);
      return;
    }
    if (item.type === 'chat') {
      handleChatSelect(item);
    }
  };

  const handleShare = async (item) => {
    const resourceType = SHARE_TYPES[item.type];
    if (!resourceType) return;
    setShareFeedback(null);
    try {
      setSharingId(item.id);
      const shareId = await createShareLink(resourceType, item.id);
      const shareUrl = `${window.location.origin}/share/${shareId}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt('Copy share link:', shareUrl);
      }
      setShareFeedback({ id: item.id, message: 'Share link copied to clipboard.' });
    } catch (err) {
      setShareFeedback({
        id: item.id,
        message: err.message || 'Failed to create share link',
        isError: true
      });
    } finally {
      setSharingId(null);
    }
  };

  const handleDelete = async (item) => {
    const meta = HISTORY_TYPES[item.type];
    if (!meta) return;
    const confirmMessage = meta.deletePrompt || `Delete this ${meta.label.toLowerCase()} result?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      setDeletingId(item.id);
      setError('');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      const response = await fetch(`${API_BASE}${DELETE_ENDPOINTS[item.type](item.id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to delete result');
      }
      setPages((prev) => prev.map((page) => ({
        ...page,
        items: (page.items || []).filter((entry) => !(entry.id === item.id && entry.type === item.type))
      })));
      if (item.type === 'chat' && onChatDeleted) {
        onChatDeleted(item.id);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete result');
    } finally {
      setDeletingId(null);
    }
  };

  const updateTitleInPages = useCallback((pagesList, itemType, itemId, nextName) => (
    pagesList.map((page) => ({
      ...page,
      items: (page.items || []).map((entry) => (
        entry.id === itemId && entry.type === itemType
          ? { ...entry, name: nextName }
          : entry
      ))
    }))
  ), []);

  const handleTitleEditStart = useCallback((item, event) => {
    event?.stopPropagation();
    titleCancelRef.current = false;
    const key = `${item.type}-${item.id}`;
    setEditingTitleKey(key);
    setEditingTitleValue(item.name || '');
  }, []);

  const clearTitleEdit = useCallback(() => {
    setEditingTitleKey(null);
    setEditingTitleValue('');
  }, []);

  const handleTitleEditCancel = useCallback(() => {
    titleCancelRef.current = true;
    clearTitleEdit();
  }, [clearTitleEdit]);

  const handleTitleCommit = useCallback(async (item) => {
    const key = `${item.type}-${item.id}`;
    if (titleCancelRef.current) {
      titleCancelRef.current = false;
      return;
    }
    if (editingTitleKey !== key || savingTitleKeys[key]) return;
    const trimmed = editingTitleValue.trim();
    const previousName = item.name || '';
    if (!trimmed) {
      setError('Title cannot be empty.');
      clearTitleEdit();
      return;
    }
    if (trimmed === previousName) {
      clearTitleEdit();
      return;
    }
    clearTitleEdit();
    setSavingTitleKeys((prev) => ({ ...prev, [key]: true }));
    setError('');
    setPages((prev) => updateTitleInPages(prev, item.type, item.id, trimmed));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      const response = await fetch(`${API_BASE}/api/history/title`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: item.type,
          id: item.id,
          name: trimmed
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to update title');
      }
      const confirmedName = (payload.name || trimmed).trim();
      if (confirmedName && confirmedName !== trimmed) {
        setPages((prev) => updateTitleInPages(prev, item.type, item.id, confirmedName));
      }
    } catch (err) {
      setError(err.message || 'Failed to update title');
      setPages((prev) => updateTitleInPages(prev, item.type, item.id, previousName));
    } finally {
      setSavingTitleKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [clearTitleEdit, editingTitleKey, editingTitleValue, savingTitleKeys, updateTitleInPages]);

  const handleStarToggle = useCallback(async (item) => {
    const starKey = `${item.type}-${item.id}`;
    const previousStarred = Boolean(item.starred);
    const nextStarred = !previousStarred;
    const updatePages = (pages, starredValue) => pages.map((page) => {
      const updatedItems = (page.items || []).map((entry) => {
        if (entry.id === item.id && entry.type === item.type) {
          return { ...entry, starred: starredValue };
        }
        return entry;
      });
      return {
        ...page,
        items: starredOnly ? updatedItems.filter((entry) => entry.starred) : updatedItems
      };
    });
    try {
      setStarringIds((prev) => ({ ...prev, [starKey]: true }));
      setError('');
      setPages((prev) => updatePages(prev, nextStarred));
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');
      const response = await fetch(`${API_BASE}/api/history/star`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: item.type,
          id: item.id,
          starred: nextStarred
        })
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.message || 'Failed to update star');
      }
      const confirmedStarred = Boolean(payload.starred ?? nextStarred);
      if (confirmedStarred !== nextStarred) {
        setPages((prev) => updatePages(prev, confirmedStarred));
      }
    } catch (err) {
      setError(err.message || 'Failed to update star');
      if (starredOnly && !nextStarred) {
        loadHistory();
      } else {
        setPages((prev) => updatePages(prev, previousStarred));
      }
    } finally {
      setStarringIds((prev) => {
        const next = { ...prev };
        delete next[starKey];
        return next;
      });
    }
  }, [loadHistory, starredOnly]);

  const historyCountLabel = useMemo(() => (
    pageItems.length === 0
      ? (starredOnly ? 'No starred runs' : 'No runs')
      : `Showing ${pageItems.length} ${starredOnly ? 'starred ' : ''}${pageItems.length === 1 ? 'run' : 'runs'}`
  ), [pageItems.length, starredOnly]);

  const renderSummary = (item) => {
    if (item.type === 'screen') {
      const summary = item.summary || {};
      return (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-xs text-gray-500">Screen Date</div>
            <div className="text-sm font-medium text-gray-900">{summary.screen_date || 'N/A'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Selected</div>
            <div className="text-sm font-medium text-gray-900">
              {summary.selected_count ?? 'N/A'} / {summary.total_universe ?? 'N/A'}
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Top Tickers</div>
            <div className="text-sm text-gray-900">
              {summary.top_tickers?.length ? summary.top_tickers.join(', ') : 'N/A'}
            </div>
          </div>
        </div>
      );
    }

    if (item.type === 'backtest' || item.type === 'optimizer') {
      const metrics = item.summary?.metrics || {};
      return (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-xs text-gray-500">CAGR</div>
            <div className="text-sm font-medium text-gray-900">{formatPercent(metrics.cagr)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Max Drawdown</div>
            <div className="text-sm font-medium text-gray-900">{formatPercent(metrics.max_drawdown)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Sharpe</div>
            <div className="text-sm font-medium text-gray-900">{formatNumber(metrics.sharpe)}</div>
          </div>
        </div>
      );
    }

    if (item.type === 'seasonality') {
      const summary = item.summary || {};
      return (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-xs text-gray-500">Symbol</div>
            <div className="text-sm font-medium text-gray-900">{summary.symbol || 'N/A'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Focus</div>
            <div className="text-sm font-medium text-gray-900">{formatFocusLabel(summary.focus)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Date Range</div>
            <div className="text-sm font-medium text-gray-900">
              {summary.start_date || 'N/A'} to {summary.end_date || 'N/A'}
            </div>
          </div>
        </div>
      );
    }

    if (item.type === 'chart') {
      const summary = item.summary || {};
      return (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-xs text-gray-500">Symbol</div>
            <div className="text-sm font-medium text-gray-900">{summary.symbol || 'N/A'}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Formula</div>
            <div className="text-sm font-medium text-gray-900">{formatFormulaLabel(summary.formula)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Date Range</div>
            <div className="text-sm font-medium text-gray-900">
              {summary.start_date || 'N/A'} to {summary.end_date || 'N/A'}
            </div>
          </div>
        </div>
      );
    }

    if (item.type === 'chat') {
      const summary = item.summary || {};
      return (
        <div>
          <div className="text-xs text-gray-500">First message</div>
          <div className="text-sm text-gray-900">{formatChatPreview(summary.first_message)}</div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="home-history">
      <div className="home-history-header">
        <div>
          <h2 className="home-history-title">History</h2>
          <p className="home-history-subtitle">
            All your screens, backtests, seasonality, charts, and chats in one timeline.
          </p>
        </div>
        <div className="home-history-actions">
          <Button
            variant="outline"
            size="sm"
            className={`flex items-center gap-1 ${starredOnly ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : ''}`}
            onClick={() => setStarredOnly((prev) => !prev)}
            aria-pressed={starredOnly}
          >
            <FiStar />
            Starred
          </Button>
          <div className="home-history-count">{historyCountLabel}</div>
        </div>
      </div>

      {error && (
        <div className="home-history-error">
          {error}
        </div>
      )}

      {loading && pages.length === 0 ? (
        <div className="home-history-loading">Loading history...</div>
      ) : (
        <>
          <div className="home-history-list" ref={listRef}>
            {pageItems.length === 0 ? (
              <div className="home-history-empty">
                {starredOnly
                  ? 'No starred history yet. Star items to pin them here.'
                  : 'No history yet. Run a screen, backtest, seasonality, chart, or chat to see it here.'}
              </div>
            ) : (
              groupedItems.map((group, groupIndex) => (
                <div key={`${group.label}-${groupIndex}`} className="home-history-group">
                  <div className="home-history-date">{group.label}</div>
                  <div className="home-history-group-items">
                    {group.items.map((item) => {
                      const meta = HISTORY_TYPES[item.type];
                      const Icon = meta?.icon;
                      const isChat = item.type === 'chat';
                      const titleKey = `${item.type}-${item.id}`;
                      const isEditingTitle = editingTitleKey === titleKey;
                      const isSavingTitle = Boolean(savingTitleKeys[titleKey]);
                      const card = (
                        <Card className="border border-gray-200">
                          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-start gap-3">
                              <span className={`flex h-10 w-10 items-center justify-center rounded-full ${meta?.badgeClass || 'bg-gray-100 text-gray-600'}`}>
                                {Icon && <Icon size={18} />}
                              </span>
                              <div>
                                <CardTitle className="text-lg font-semibold">
                                  {isEditingTitle ? (
                                    <input
                                      type="text"
                                      value={editingTitleValue}
                                      onChange={(event) => setEditingTitleValue(event.target.value)}
                                      onBlur={() => handleTitleCommit(item)}
                                      onClick={(event) => event.stopPropagation()}
                                      onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                          event.preventDefault();
                                          event.currentTarget.blur();
                                        }
                                        if (event.key === 'Escape') {
                                          event.preventDefault();
                                          handleTitleEditCancel();
                                          event.currentTarget.blur();
                                        }
                                      }}
                                      className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 text-lg font-semibold text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                                      maxLength={120}
                                      autoFocus
                                      disabled={isSavingTitle}
                                      aria-label="Edit history title"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-left text-lg font-semibold text-gray-900 hover:underline"
                                      onClick={(event) => handleTitleEditStart(item, event)}
                                      disabled={isSavingTitle}
                                    >
                                      {item.name}
                                    </button>
                                  )}
                                </CardTitle>
                                <div className="mt-1 text-xs text-gray-500">
                                  {(meta?.label || 'Run')} · Created {formatDate(item.created_at)}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleStarToggle(item);
                                }}
                                className={`flex items-center gap-1 ${item.starred ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : ''}`}
                                aria-pressed={item.starred}
                                aria-label={item.starred ? 'Unstar item' : 'Star item'}
                                disabled={Boolean(starringIds[`${item.type}-${item.id}`])}
                              >
                                <FiStar />
                                {item.starred ? 'Starred' : 'Star'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleView(item);
                                }}
                                className="flex items-center gap-1"
                              >
                                <FiEye />
                                View
                              </Button>
                              {SHARE_TYPES[item.type] && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleShare(item);
                                  }}
                                  className="flex items-center gap-1"
                                  disabled={sharingId === item.id}
                                >
                                  <FiLink />
                                  {sharingId === item.id ? 'Sharing...' : 'Share'}
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleDelete(item);
                                }}
                                className="flex items-center gap-1 text-red-600 hover:text-red-700"
                                disabled={deletingId === item.id}
                              >
                                <FiTrash2 />
                                {deletingId === item.id ? 'Deleting...' : 'Delete'}
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {renderSummary(item)}
                            {shareFeedback?.id === item.id && (
                              <div className={`mt-3 text-xs ${shareFeedback.isError ? 'text-red-600' : 'text-emerald-600'}`}>
                                {shareFeedback.message}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );

                      if (isChat) {
                        return (
                          <div
                            key={`${item.type}-${item.id}`}
                            className="home-history-card home-history-card-clickable"
                            role="button"
                            tabIndex={0}
                            aria-label={`Open ${item.name}`}
                            onClick={() => {
                              if (editingTitleKey === titleKey) return;
                              handleChatSelect(item);
                            }}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                if (editingTitleKey !== titleKey) {
                                  handleChatSelect(item);
                                }
                              }
                            }}
                          >
                            {card}
                          </div>
                        );
                      }

                      return (
                        <div key={`${item.type}-${item.id}`} className="home-history-card">
                          {card}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
            {(pages.length > 1 || currentPage.nextCursor) && (
              <div className="home-history-pagination">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevPage}
                  disabled={pageIndex === 0 || paging}
                >
                  Previous
                </Button>
                <span className="home-history-page-label">Page {pageIndex + 1}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!currentPage.nextCursor || paging}
                >
                  {paging ? 'Loading...' : 'Next'}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default HistorySection;
